import { Component, ElementRef, forwardRef, HostListener, input, output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { VaultDocument } from '../../../models/vaultDocument';

@Component({
  selector: 'app-vault-document-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './vault-document-select.component.html',
  styleUrls: ['./vault-document-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VaultDocumentSelectComponent),
      multi: true,
    },
  ],
})
export class VaultDocumentSelectComponent implements ControlValueAccessor {
  readonly minHeight = input<number>(24);
  readonly editMode = input<boolean>(false);
  readonly documents = input<VaultDocument[]>([]);

  readonly viewVaultDocument = output<VaultDocument>();
  readonly addVaultDocument = output<void>();
  readonly removeVaultDocument = output<VaultDocument>();

  public isOpen = false;
  public disabled = false;
  private _documents: VaultDocument[] = [];

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documents']) {
      this._documents = this.documents() ?? [];
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  public toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
        setTimeout(() => {
            this.positionDropdown();
            const dropdown = this.elementRef.nativeElement.querySelector('.vault-doc-dropdown');
            if (dropdown) dropdown.style.visibility = 'visible';
        });
    }
  }

  private positionDropdown(): void {
      const trigger = this.elementRef.nativeElement.querySelector('.vault-doc-trigger');
      const dropdown = this.elementRef.nativeElement.querySelector('.vault-doc-dropdown');
      if (!trigger || !dropdown) return;
  
      dropdown.style.visibility = 'hidden';
      const rect: DOMRect = trigger.getBoundingClientRect();
      dropdown.style.top = `${rect.bottom}px`;
      dropdown.style.left = `${rect.left}px`;
      dropdown.style.width = `${rect.width}px`;
  }

  get currentDocuments(): VaultDocument[] {
    return this._documents;
  }

  public viewDocument(doc: VaultDocument): void {
    if (!this.disabled) {
      this.viewVaultDocument.emit(doc);
      this.isOpen = false;
    }
  }

  public removeDocument(doc: VaultDocument, event: MouseEvent): void {
    event.stopPropagation();
    this.onChange(this._documents);
    this.onTouched();
    this.removeVaultDocument.emit(doc);
  }

  public uploadDocument(): void {
    if (!this.disabled) {
      this.addVaultDocument.emit();
      this.isOpen = false;
    }
  }

  /**
   * Call this from the parent after a successful upload to push
   * the new document into the list (and notify the form).
   */
  public addDocument(doc: VaultDocument): void {
    if (!this._documents.find(d => d.id === doc.id)) {
      this._documents = [...this._documents, doc];
      this.onChange(this._documents);
    }
  }

  // ── ControlValueAccessor ────────────────────────────────────────
  writeValue(value: VaultDocument[] | null): void {
    this._documents = value ?? [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}